import React, { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb,
  Button,
  Col,
  Container,
  FormControl,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Table,
  Form,
} from "react-bootstrap";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { IoSearchSharp } from "react-icons/io5";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePermissions } from "carehub_auth_permissions";
import Loader from "../../ui/Layout/Loader";
import { fetchData, patchData } from "../../api/apiService";

const itemsPerPage = 10;
const outboundPendingFileApiPath = "/outbound-pending-file";

const formatFileSize = (bytes) => {
  if (!bytes) {
    return "0 MB";
  }

  const megabytes = Number(bytes) / (1024 * 1024);
  return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)} MB`;
};

const formatDateTime = (dateTime) => {
  if (!dateTime) {
    return "-";
  }

  return new Date(dateTime).toLocaleString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const OutboundPendingFile = () => {
  const { permissions, permissionsLoaded } = usePermissions();
  const navigate = useNavigate();
  const [outboundFiles, setOutboundFiles] = useState([]);
  const [outboundFilesCount, setOutboundFilesCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [fileToFail, setFileToFail] = useState(null);
  const [statusDescription, setStatusDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const totalPages = Math.max(1, Math.ceil(outboundFilesCount / itemsPerPage));
  const canViewPendingFiles =
    permissions?.includes("admin_outbound_pending_file_list") ||
    permissions?.includes("administration_manage_outbound_pending_files_status");
  const canEditPendingFile = permissions?.includes(
    "admin_outbound_pending_file_edit"
  );

  const loadPendingOutboundFiles = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      });

      if (searchTerm?.trim()) {
        queryParams.append("search", searchTerm.trim());
      }

      const response = await fetchData(
        `${outboundPendingFileApiPath}?${queryParams.toString()}`
      );
      setOutboundFiles(response.outboundFiles || []);
      setOutboundFilesCount(response.outboundFilesCount || 0);
    } catch (error) {
      if (error.statusCode === 403) {
        navigate("/403", { replace: true });
        return;
      }
      toast.error(error?.message || "Failed to load pending outbound files.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, navigate, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  useEffect(() => {
    if (
      permissionsLoaded &&
      canViewPendingFiles
    ) {
      loadPendingOutboundFiles();
    }
  }, [canViewPendingFiles, permissionsLoaded, loadPendingOutboundFiles]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setCurrentPage(1);
    if (sortBy === column) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
      return;
    }

    setSortBy(column);
    setSortOrder("ASC");
  };

  const getSortIcon = (column) => {
    if (sortBy === column) {
      return sortOrder === "ASC" ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const handleShowFailConfirmation = (file) => {
    setFileToFail(file);
    setStatusDescription(file?.statusDescription || "");
  };

  const handleCloseFailConfirmation = () => {
    setFileToFail(null);
    setStatusDescription("");
  };

  const handleConfirmMarkFailed = async () => {
    if (!fileToFail) {
      return;
    }

    setUpdating(true);
    try {
      await patchData(
        `${outboundPendingFileApiPath}/${fileToFail.id}/mark-failed`,
        { statusDescription }
      );
      toast.success("Outbound file marked as failed.");
      handleCloseFailConfirmation();
      await loadPendingOutboundFiles();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to mark outbound file as failed.";
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const renderPaginationItems = () => {
    const maxPageNumbersToShow = 5;
    const halfPageNumbersToShow = Math.floor(maxPageNumbersToShow / 2);
    let startPage = Math.max(currentPage - halfPageNumbersToShow, 1);
    let endPage = Math.min(startPage + maxPageNumbersToShow - 1, totalPages);

    if (endPage - startPage + 1 < maxPageNumbersToShow) {
      startPage = Math.max(endPage - maxPageNumbersToShow + 1, 1);
    }

    const pages = [];
    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    return (
      <>
        {startPage > 1 && <Pagination.Ellipsis disabled />}
        {pages}
        {endPage < totalPages && <Pagination.Ellipsis disabled />}
      </>
    );
  };

  if (!permissionsLoaded) {
    return <Loader />;
  }

  return (
    <div>
      {canViewPendingFiles ? (
        <>
          <Container className="mt-2 px-0">
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link className="text-dark link" to="/administration">
                  Administration
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item active>
                Manage Outbound Pending Files Status
              </Breadcrumb.Item>
            </Breadcrumb>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3>Manage Outbound Pending Files Status</h3>
            </div>
          </Container>

          <Container className="border rounded px-4 pt-3 mt-2">
            <ToastContainer />
            <Row className="mb-3 justify-content-end align-items-center">
              <Col md={4}>
                <InputGroup size="sm">
                  <FormControl
                    placeholder="Search by file name"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <InputGroup.Text style={{ cursor: "pointer" }}>
                    <IoSearchSharp />
                  </InputGroup.Text>
                </InputGroup>
              </Col>
            </Row>

            {loading ? (
              <Loader />
            ) : (
              <Table
                striped
                bordered
                hover
                size="sm"
                className="pvp-list-table outbound-pending-file-table"
              >
                <colgroup>
                  <col className="outbound-pending-index-col" />
                  <col className="outbound-pending-file-name-col" />
                  <col className="outbound-pending-size-col" />
                  <col className="outbound-pending-created-col" />
                  <col className="outbound-pending-status-col" />
                  {canEditPendingFile && (
                    <col className="outbound-pending-action-col" />
                  )}
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th
                      onClick={() => handleSort("fileName")}
                      style={{ cursor: "pointer" }}
                    >
                      File Name {getSortIcon("fileName")}
                    </th>
                    <th
                      onClick={() => handleSort("size")}
                      style={{ cursor: "pointer" }}
                    >
                      Size {getSortIcon("size")}
                    </th>
                    <th
                      onClick={() => handleSort("createdAt")}
                      style={{ cursor: "pointer" }}
                    >
                      Created At {getSortIcon("createdAt")}
                    </th>
                    <th>Current Status</th>
                    {canEditPendingFile && <th className="text-end">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {outboundFiles.length > 0 ? (
                    outboundFiles.map((file, index) => (
                      <tr key={file.id}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="text-break">{file.fileName}</td>
                        <td>{formatFileSize(file.size)}</td>
                        <td className="outbound-pending-date-cell">
                          {formatDateTime(file.createdAt)}
                        </td>
                        <td>{file.fileStatus || "-"}</td>
                        {canEditPendingFile && (
                          <td className="outbound-pending-action-cell">
                            <Button
                              variant="danger"
                              size="sm"
                              className="outbound-pending-action-button"
                              onClick={() => handleShowFailConfirmation(file)}
                              disabled={updating}
                            >
                              Mark Failed
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={canEditPendingFile ? "6" : "5"}
                        className="text-center"
                      >
                        No pending outbound files found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}

            <div className="d-flex justify-content-between align-items-center">
              <div className="font-size-14">
                Page {currentPage} of {totalPages}
              </div>
              <Pagination className="d-grid p-0 d-md-flex justify-content-md-end">
                <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                {renderPaginationItems()}
                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          </Container>

          <Modal
            show={Boolean(fileToFail)}
            onHide={handleCloseFailConfirmation}
            backdropClassName="outbound-pending-modal-backdrop"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Confirm Status Change</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to mark this outbound file as failed?
              {fileToFail?.fileName && (
                <div className="mt-2 text-break">
                  <strong>File:</strong> {fileToFail.fileName}
                </div>
              )}
              <Form.Group className="mt-3" controlId="statusDescription">
                <Form.Label>Status Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={statusDescription}
                  onChange={(event) =>
                    setStatusDescription(event.target.value)
                  }
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleCloseFailConfirmation}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmMarkFailed}
                disabled={updating}
              >
                {updating ? "Updating..." : "Mark Failed"}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      ) : (
        <Navigate to="/401" replace />
      )}
    </div>
  );
};

export default OutboundPendingFile;
